import { Inject, Injectable } from "@angular/core";
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from "@angular/common/http";
import { Observable, throwError, switchMap, catchError } from "rxjs";
import { AUTH_TOKEN_PROVIDER, AuthTokenProviderPort } from 'src/@shared/shared/providers';
import { Router } from "@angular/router";
import { SessionService } from "src/@services/session.service";
import { isJwtExpired, TokenRefreshService } from "src/@shared/auth";
import { AuthEndpoints } from "src/@shared/shared/endpoints/auth.endpoints";

@Injectable({
  providedIn: "root",
})
export class AuthInterceptorService implements HttpInterceptor {
  constructor(
    @Inject(AUTH_TOKEN_PROVIDER) private authTokenProvider: AuthTokenProviderPort,
    private tokenRefresh: TokenRefreshService,
    private sessionService: SessionService,
    private router: Router
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (request.headers.has('Authorization') || request.url.endsWith(AuthEndpoints.Refresh)) {
      return next.handle(request);
    }

    const token = this.authTokenProvider.getAccessToken();
    return next.handle(this.withBearer(request, token)).pipe(
      catchError((error: unknown) => {
        if (!token || !this.isExpiredTokenResponse(error, token)) {
          return throwError(() => error);
        }
        return this.tokenRefresh.refresh().pipe(
          switchMap((freshToken) => next.handle(this.withBearer(request, freshToken))),
          catchError((refreshError: unknown) => {
            this.sessionService.clearSession();
            void this.router.navigate(['/login']);
            return throwError(() => refreshError);
          })
        );
      })
    );
  }

  private isExpiredTokenResponse(error: unknown, token: string): boolean {
    return error instanceof HttpErrorResponse
      && error.status === 401
      && (error.headers.get('Token-Expired') === 'true' || isJwtExpired(token));
  }

  private withBearer(request: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });
  }
}
